import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, Checkbox, Spin, Typography, Divider, Space, Progress, InputNumber, Tooltip, Modal } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MailOutlined, UserOutlined, GlobalOutlined, DollarOutlined, SafetyOutlined, ClockCircleOutlined, QuestionCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { updateLanguage } from '../i18n';
import { TermsContentEN } from './TermsContentEN';
import { TermsContentES } from './TermsContentES';
import './AcceptInvite.css';

const { Title, Text, Paragraph } = Typography;

interface InviteDetails {
  email: string;
  full_name: string;
  country: string;
  min_required_contribution_cents: number;
  code_truncated: string;
  hours_remaining: number;
  expires_at: string;
  locale: string;
  locale_bootstrap?: { // S37: Server-provided locale with source
    locale: string;
    locale_source: string;
  };
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const code = searchParams.get('code');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [termsLanguage, setTermsLanguage] = useState<'en' | 'es'>('en');

  // Password requirements (3 of 4 must be met)
  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    { label: 'At least 8 characters', test: (p: string) => p.length >= 8, met: false },
    { label: 'Contains uppercase letter (A-Z)', test: (p: string) => /[A-Z]/.test(p), met: false },
    { label: 'Contains lowercase letter (a-z)', test: (p: string) => /[a-z]/.test(p), met: false },
    { label: 'Contains number (0-9)', test: (p: string) => /[0-9]/.test(p), met: false },
    { label: 'Contains special character (!@#$%...)', test: (p: string) => /[^A-Za-z0-9]/.test(p), met: false },
  ]);

  // Update password requirements as user types
  useEffect(() => {
    const updated = requirements.map(req => ({
      ...req,
      met: req.test(password)
    }));
    setRequirements(updated);
  }, [password]);

  // Track when terms link is clicked
  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsModalVisible(true);
    setTermsViewed(true);
  };

  const handleTermsModalClose = () => {
    setTermsModalVisible(false);
  };

  const toggleTermsLanguage = () => {
    setTermsLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  // Calculate password strength (excluding length requirement, checking complexity)
  const passwordStrength = requirements.slice(1).filter(r => r.met).length;
  const isPasswordValid = requirements[0].met && passwordStrength >= 3;

  // Load invite details on mount
  useEffect(() => {
    if (!code) {
      navigate('/invite/status?status=invalid', { replace: true });
      return;
    }

    fetchInviteDetails();
  }, [code]);

  const fetchInviteDetails = async () => {
    try {
      setLoading(true);

      const data = await api.invites.getInfo(code!);
      
      // S37: Set language based on invite locale (with fallback to old format)
      const inviteLocale = data.locale_bootstrap?.locale || data.locale;
      if (inviteLocale) {
        updateLanguage(inviteLocale);
      }
      
      setInviteDetails({
        ...data,
        code_truncated: code!.substring(0, 8) + '...',
        expires_at: new Date(Date.now() + data.hours_remaining * 60 * 60 * 1000).toISOString(),
      });
    } catch (err: any) {
      const response = err.response;
      if (response?.status === 410) {
        // Expired invite - redirect to status page
        navigate(`/invite/status?code=${code}&status=expired`, { replace: true });
      } else if (response?.status === 409 && response.data.account_exists) {
        // Account already exists - redirect to status page
        navigate(`/invite/status?code=${code}&status=used`, { replace: true });
      } else if (response?.status === 404) {
        // Invite not found - redirect to status page
        navigate(`/invite/status?code=${code}&status=not-found`, { replace: true });
      } else {
        // Invalid invite - redirect to status page
        navigate(`/invite/status?code=${code}&status=invalid`, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      const data = await api.invites.accept({
        code: code!,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });

      if (data.message) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (err: any) {
      // Error handling is done by the API client
      console.error('Failed to create account:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  // Loading state
  if (loading) {
    return (
      <div className="accept-invite-container">
        <Card className="accept-invite-card">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <Paragraph style={{ marginTop: 24, color: '#8c8c8c' }}>
              Verifying invitation...
            </Paragraph>
          </div>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="accept-invite-container">
        <Card className="accept-invite-card">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <Title level={3} style={{ marginTop: 24, color: '#ffffff' }}>
              Account Created Successfully!
            </Title>
            <Paragraph style={{ color: '#8c8c8c', marginTop: 16 }}>
              Redirecting to your dashboard...
            </Paragraph>
          </div>
        </Card>
      </div>
    );
  }

  // If no invite details and no loading, something went wrong - should have redirected
  if (!inviteDetails) {
    return null;
  }

  // Main form
  return (
    <div className="accept-invite-container">
      <Card className="accept-invite-card">
        <Title level={2} style={{ color: '#ffffff', textAlign: 'center', marginBottom: 8 }}>
          {t('acceptInvite.title')}
        </Title>
        <Paragraph style={{ color: '#8c8c8c', textAlign: 'center', marginBottom: 32 }}>
          {t('acceptInvite.subtitle')}
        </Paragraph>

        {inviteDetails && (
          <>
            {/* Invite Details Panel */}
            <div className="invite-details-panel">
              <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                <div className="detail-row">
                  <MailOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.emailLabel')}</Text>
                    <Text className="detail-value">{inviteDetails.email}</Text>
                  </div>
                </div>
                
                <div className="detail-row">
                  <UserOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.nameLabel')}</Text>
                    <Text className="detail-value">{inviteDetails.full_name}</Text>
                  </div>
                </div>
                
                <div className="detail-row">
                  <GlobalOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.countryLabel')}</Text>
                    <Text className="detail-value">{inviteDetails.country === 'USA' ? t('acceptInvite.countryUSA') : t('acceptInvite.countryNonUSA')}</Text>
                  </div>
                </div>
                
                <div className="detail-row">
                  <DollarOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.minimumContribution')}</Text>
                    <Text className="detail-value">{formatCurrency(inviteDetails.min_required_contribution_cents)}</Text>
                  </div>
                </div>
                
                <div className="detail-row">
                  <SafetyOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.invitationCode')}</Text>
                    <Text className="detail-value code-truncated">{inviteDetails.code_truncated}</Text>
                  </div>
                </div>
                
                <div className="detail-row expiry-row">
                  <ClockCircleOutlined className="detail-icon" />
                  <div className="detail-content">
                    <Text className="detail-label">{t('acceptInvite.timeRemaining')}</Text>
                    <Text className="detail-value" style={{ color: inviteDetails.hours_remaining < 2 ? '#ff4d4f' : '#52c41a' }}>
                      {inviteDetails.hours_remaining} {t('acceptInvite.hours')}
                    </Text>
                  </div>
                </div>
              </Space>
            </div>

            <Divider style={{ borderColor: '#303030', margin: '32px 0' }} />

            {/* Password Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                name="password"
                label={<span style={{ color: '#ffffff' }}>{t('acceptInvite.passwordLabel')}</span>}
                rules={[
                  { required: true, message: t('acceptInvite.errors.passwordRequired') },
                  { min: 8, message: t('acceptInvite.errors.passwordTooShort') },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const complexityCount = [
                        /[A-Z]/.test(value),
                        /[a-z]/.test(value),
                        /[0-9]/.test(value),
                        /[^A-Za-z0-9]/.test(value),
                      ].filter(Boolean).length;
                      if (complexityCount < 3) {
                        return Promise.reject(t('acceptInvite.errors.passwordComplexity'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder={t('acceptInvite.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                />
              </Form.Item>

              {/* Password Requirements Checklist */}
              <div className="password-requirements">
                <Text style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 8, display: 'block' }}>
                  {t('acceptInvite.passwordRequirementsTitle')}
                </Text>
                {requirements.map((req, idx) => (
                  <div key={idx} className="requirement-item">
                    {req.met ? (
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#434343', fontSize: 14 }} />
                    )}
                    <Text style={{ color: req.met ? '#52c41a' : '#8c8c8c', fontSize: 12, marginLeft: 8 }}>
                      {t(`acceptInvite.requirement${idx + 1}`)}
                    </Text>
                  </div>
                ))}
                {password.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text style={{ color: '#8c8c8c', fontSize: 12 }}>
                      {t('acceptInvite.complexityLabel', { count: passwordStrength })}
                    </Text>
                    <Progress
                      percent={(passwordStrength / 4) * 100}
                      showInfo={false}
                      strokeColor={passwordStrength >= 3 ? '#52c41a' : '#faad14'}
                      size="small"
                      style={{ marginTop: 4 }}
                    />
                  </div>
                )}
              </div>

              <Form.Item
                name="password_confirmation"
                label={<span style={{ color: '#ffffff' }}>{t('acceptInvite.confirmPasswordLabel')}</span>}
                dependencies={['password']}
                rules={[
                  { required: true, message: t('acceptInvite.errors.passwordConfirmRequired') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t('acceptInvite.errors.passwordMismatch'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder={t('acceptInvite.confirmPasswordPlaceholder')}
                  className="input-field"
                />
              </Form.Item>

              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value ? Promise.resolve() : Promise.reject(t('acceptInvite.errors.termsRequired')),
                  },
                ]}
              >
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={!termsViewed}
                  style={{ color: '#ffffff' }}
                >
                  {t('acceptInvite.termsLabel')}{' '}
                  <a 
                    href="#"
                    onClick={handleTermsClick}
                    style={{ fontWeight: 600, textDecoration: 'underline' }}
                  >
                    {t('acceptInvite.termsLink')}
                  </a>
                  {!termsViewed && (
                    <Tooltip title="Please click to view the Terms and Conditions before accepting">
                      <QuestionCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitting}
                  disabled={!isPasswordValid || !termsAccepted || !termsViewed}
                  block
                  className="submit-button"
                >
                  {submitting ? t('acceptInvite.creating') : t('acceptInvite.submitButton')}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Card>

      {/* Terms and Conditions Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>
              {termsLanguage === 'en' 
                ? 'DivvyStash – Terms and Conditions of Use'
                : 'DivvyStash – Términos y Condiciones de Uso'
              }
            </span>
          </Space>
        }
        open={termsModalVisible}
        onCancel={handleTermsModalClose}
        footer={[
          <Button key="language" onClick={toggleTermsLanguage} icon={<GlobalOutlined />}>
            {termsLanguage === 'en' ? 'Español' : 'English'}
          </Button>,
          <Button key="close" type="primary" onClick={handleTermsModalClose}>
            Close
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
        styles={{ body: {
            maxHeight: 'calc(100vh - 200px)', 
            overflowY: 'auto',
            padding: '24px'
          }
        }}
      >
        <div style={{ color: '#ffffff', lineHeight: 1.8 }}>
          <Text style={{ fontSize: 14, color: '#8c8c8c', fontStyle: 'italic', display: 'block', marginBottom: 24 }}>
            {termsLanguage === 'en' 
              ? 'Last updated: 2025-12-13'
              : 'Última actualización: 2025-12-13'
            }
          </Text>
          {termsLanguage === 'en' ? <TermsContentEN /> : <TermsContentES />}
        </div>
      </Modal>
    </div>
  );
};

export default AcceptInvite;
